import { Link } from '@tanstack/react-router';
import { Network } from 'lucide-react';
import { Button, ErrorNotice, Spinner } from '@nw/ui';
import { PinButton, useEntity, useExplore } from '@nw/web-platform';

/** Generic page for shared entities without a module-specific view (e.g. people). */
export function EntityPage({ entityKey }: { entityKey: string }) {
  const q = useEntity(entityKey);
  const explore = useExplore(entityKey);
  if (q.isPending) return <Spinner className="m-10" />;
  if (q.isError) return <ErrorNotice className="m-10" title="This item is not available" detail="It may have been deleted, or its module is turned off." />;
  const { entity, relations, related } = q.data;
  const groups = new Map<string, Array<{ id: string; other: (typeof related)[string] }>>();
  for (const r of relations) {
    const outgoing = r.from === entity.key;
    const otherKey = outgoing ? r.to : r.from;
    const other = related[otherKey];
    if (!other) continue;
    const label = outgoing
      ? r.predicateLabel
      : ({
          'written by': 'wrote',
          'note on': 'has note',
          'is about': 'is the subject of',
          supports: 'is supported by',
          'leads to': 'follows from',
        }[r.predicateLabel] ?? r.predicateLabel);
    (groups.get(label) ?? groups.set(label, []).get(label)!).push({
      id: r.id,
      other,
    });
  }
  return (
    <div className="mx-auto w-full max-w-3xl px-5 pt-6 pb-20 sm:px-8">
      <p className="text-[var(--nw-quiet)]">{entity.typeLabel}</p>
      <h1 className="font-display text-[2.6rem] leading-tight">{entity.title}</h1>
      {entity.summary ? <p className="mt-2 text-lg text-[var(--nw-quiet)]">{entity.summary}</p> : null}
      <div className="mt-5 flex flex-wrap gap-2">
        {explore ? (
          <Button asChild variant="primary">
            <Link to={explore.to} search={explore.search}>
              <Network /> Explore connections
            </Link>
          </Button>
        ) : null}
        <PinButton entityRef={entity.ref} label={entity.title} />
      </div>
      <div className="mt-10 grid gap-8">
        {groups.size === 0 ? <p className="text-[var(--nw-quiet)]">No connections yet.</p> : null}
        {[...groups.entries()].map(([label, list]) => (
          <section key={label}>
            <h2 className="font-display text-xl first-letter:uppercase">{label}</h2>
            <ul className="mt-2 divide-y divide-[var(--nw-line)] border-y border-[var(--nw-line)]">
              {list.map(({ id, other }) => (
                <li key={id} className="py-2.5">
                  <Link to={other.href} className="font-medium hover:underline">
                    {other.title}
                  </Link>
                  <span className="text-sm text-[var(--nw-quiet)]"> {other.typeLabel.toLowerCase()}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
